import {
  ENV,
  authFetch,
  parseResponse,
  SUCCESSARRAY,
} from "../utils";

function normalizeString(value) {
  return String(value || "").trim();
}

function normalizeObjectId(value) {
  if (
    !value ||
    value === "null" ||
    value === "undefined"
  ) {
    return null;
  }

  if (typeof value === "object") {
    const id =
      value._id ||
      value.id ||
      value.productId ||
      value.variantId ||
      null;

    if (
      !id ||
      id === "null" ||
      id === "undefined"
    ) {
      return null;
    }

    return String(id).trim();
  }

  return String(value).trim();
}

function normalizePositiveInteger(value) {
  const numberValue = Number(value);

  if (!Number.isInteger(numberValue) || numberValue < 1) {
    return null;
  }

  return numberValue;
}

function normalizeNonNegativeNumber(value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const numberValue = Number(value);

  if (!Number.isFinite(numberValue) || numberValue < 0) {
    return null;
  }

  return numberValue;
}

function normalizeItems(
  items = [],
  includeUnitCost = false,
  includeUnitPrice = false
) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item = {}) => {
    const normalizedItem = {
      product: normalizeObjectId(
        item.product || item.productId
      ),
      variantId: normalizeObjectId(
        item.variantId || item.variant
      ),
      quantity: normalizePositiveInteger(item.quantity),
    };

    if (includeUnitCost) {
      normalizedItem.unitCost =
        item.unitCost === "" ||
        item.unitCost === null ||
        item.unitCost === undefined
          ? null
          : normalizeNonNegativeNumber(item.unitCost);
    }

    if (includeUnitPrice) {
      normalizedItem.unitPrice =
        item.unitPrice === "" ||
        item.unitPrice === null ||
        item.unitPrice === undefined
          ? null
          : normalizeNonNegativeNumber(item.unitPrice);
    }

    return normalizedItem;
  });
}

function cleanFilters(filters = {}) {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== ""
    )
  );
}

function getInventoryUrl(endpoint) {
  return `${ENV.API_URL}/${ENV.ENDPOINTS.INVENTORY[endpoint]}`;
}

async function request(url, params = {}) {
  const hasBody =
    params.body !== undefined &&
    params.body !== null;

  const body =
    hasBody && typeof params.body === "string"
      ? new Blob([params.body], {
          type: "application/json",
        })
      : params.body;

  const response = await authFetch(url, {
    ...params,
    body,
    headers: {
      ...(params.headers || {}),
      ...(hasBody
        ? {
            "Content-Type": "application/json",
            Accept: "application/json",
          }
        : {
            Accept: "application/json",
          }),
    },
  });

  const result = await parseResponse(response);

  if (!SUCCESSARRAY.includes(response.status)) {
    throw result;
  }

  return result;
}

export class Inventory {
  async getExistences(page = 1, limit = 10, filters = {}) {
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      ...cleanFilters(filters),
    });

    const url = `${getInventoryUrl(
      "EXISTENCES"
    )}?${queryParams.toString()}`;

    return request(url);
  }

  async getSummary(filters = {}) {
    const queryParams = new URLSearchParams(cleanFilters(filters));
    const query = queryParams.toString();

    const url = `${getInventoryUrl("SUMMARY")}${query ? `?${query}` : ""}`;

    return request(url);
  }

  async getMovements(page = 1, limit = 10, filters = {}) {
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      ...cleanFilters(filters),
    });

    const url = `${getInventoryUrl(
      "MOVEMENTS"
    )}?${queryParams.toString()}`;

    return request(url);
  }

  async createEntry(data = {}) {
    const url = getInventoryUrl("ENTRIES");

    const normalizedItems = normalizeItems(data.items, true);

    console.log(
      "createEntry - items originales:",
      data.items
    );

    console.log(
      "createEntry - items normalizados:",
      normalizedItems
    );

    const payload = {
      reference: normalizeString(data.reference),
      notes: normalizeString(data.notes),
      items: normalizedItems,
    };

    console.log(
      "createEntry - payload:",
      JSON.stringify(payload, null, 2)
    );

    return request(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async createExit(data = {}) {
    const url = getInventoryUrl("EXITS");

    const payload = {
      reference: normalizeString(data.reference),
      notes: normalizeString(data.notes),
      saleDate: normalizeString(data.saleDate),
      items: normalizeItems(data.items, false, true),
    };

    return request(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getProfitSummary({ year, months } = {}) {
    const queryParams = new URLSearchParams(
      year !== undefined
        ? { year: String(year) }
        : { months: String(months || 6) }
    );

    const url = `${getInventoryUrl(
      "PROFIT_SUMMARY"
    )}?${queryParams.toString()}`;

    return request(url);
  }

  async getMonthlySalesDetail(year, month) {
    const queryParams = new URLSearchParams({
      year: String(year),
      month: String(month),
    });

    const url = `${getInventoryUrl(
      "MONTHLY_SALES_DETAIL"
    )}?${queryParams.toString()}`;

    return request(url);
  }

  async getYearlySalesDetail(year) {
    const queryParams = new URLSearchParams({
      year: String(year),
    });

    const url = `${getInventoryUrl(
      "YEARLY_SALES_DETAIL"
    )}?${queryParams.toString()}`;

    return request(url);
  }

  async createAdjustment(data = {}) {
    const url = getInventoryUrl("ADJUSTMENTS");

    const adjustmentType =
      String(data.adjustmentType || "").toUpperCase() === "OUT"
        ? "OUT"
        : "IN";

    const payload = {
      adjustmentType,
      reference: normalizeString(data.reference),
      notes: normalizeString(data.notes),
      items: normalizeItems(data.items, true),
    };

    return request(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }
}